import React, { useEffect, useReducer, useState } from 'react';
import { IconButton, Button, Tabs, Tab } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import { createSpaCategoryAPI, updateSpaCategoryAPI } from '../../../../../utils/PropertyAPIUtils';
import { notificationManager } from '../../../../../utils/NotificationUtils';
import CustomTextField from '../../../../../shared/form-controls/custom-text-field/Custom-Text-Field';
import { useDispatch } from 'react-redux';
import {
  fetchAllSpa,
  allSpaInfoSelector,
  allHotelsSelector,
} from '../../../../property/propertySlice';
import { formDataValidate } from '../../../../../utils/formUtils';
import { useSelector } from 'react-redux';
import CustomSelect from '../../../../../shared/form-controls/custom-select/Custom-Select';
import ImageUpload from '../../../../../shared/image-upload/ImageUpload';
import LinearProgressBar from '../../../../../shared/progress-bar/Progress';
import { getProcessedImages } from '../../../../../shared/image-upload/ImageProcessor';
import { SPA_TYPE } from '../../../../../constants/SpaConst';
import KeyValuePairList from '../../../../../shared/key-value-pair-list/KeyValuePairList';
import { decryptLocalStorage } from '../../../../../utils/authUtils';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../../../../utils/loaderSlice';
import { getPreSignedUrl } from '../../../../../utils/PropertyAPIUtils';
import { convertFileToBase64 } from '../../../../../utils/commonUtils';
import { ASSET_URL } from '../../../../../env/Configurations';
import { ASSET_TYPE_PROPERTY } from '../../../../../constants/PropertyConst';
import {
  decodeMediaKeyValue,
  renderVideos,
  renderGifs,
  toMediaContent,
  processGifsForUpload,
} from '../../../../../utils/mediaUtils';

const CategoryCreate = (props) => {
  const [tabValue, setTabValue] = useState(0);
  const originalHotels = useSelector(allHotelsSelector);
  const [rawImages, setRawImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const { toggleDrawer, category, hotelId, showLoader = () => {} } = props;
  const dispatch = useDispatch();
  const originalSpaInfo = useSelector(allSpaInfoSelector);
  const [isTabInValid, setIsTabInValid] = useState(true);
  const [rawVideos, setRawVideos] = useState([]);
  const [rawGifs, setRawGifs] = useState([]);
  const [videoImageUrl, setVideoImageUrl] = useState('');
  const [gifImageUrl, setGifImageUrl] = useState('');
  const [hasVideoError, setHasVideoError] = useState(false);
  const [isThumbnailUploading, setIsThumbnailUploading] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [isGifLoading, setIsGifLoading] = useState(false);
  const groupId = originalHotels?.find((h) => h?.id === hotelId)?.groupId;
  const [formData, setFormData] = useReducer((state, newState) => ({ ...state, ...newState }), {
    spaId: {
      value: '',
      fieldName: 'Select Spa',
      errors: {},
      validations: ['required'],
      tab: 0,
    },
    name: {
      value: '',
      fieldName: 'Name',
      errors: {},
      validations: ['required'],
      tab: 0,
    },
    customAttributes: {
      value: [],
      fieldName: 'Custom Attributes',
      errors: {},
      validations: [],
      tab: 0,
    },
    code: {
      value: '',
      fieldName: 'Code',
      errors: {},
      validations: ['required', 'validAlphaNumeric'],
      tab: 0,
    },
    images: {
      value: [],
      fieldName: 'Images',
      errors: {},
      validations: [],
      tab: 1,
    },
  });

  useEffect(() => {
    const isValid = formDataValidate(formData, false);
    setIsTabInValid(!isValid);
  }, [formData]);

  useEffect(() => {
    if (category) {
      const currFields = { ...formData };
      currFields.code.value = category?.code;
      currFields.name.value = category?.name;
      currFields.spaId.value = category?.spaId;
      currFields.images.value = category?.images || [];
      currFields.customAttributes.value = category?.customAttributes || [];
      const { Video: videoMediaContent, GIF: gifMediaContent } = decodeMediaKeyValue(
        category?.mediaContent,
      );
      const videoData = videoMediaContent || category?.videos || {};
      const gifData = gifMediaContent || category?.gifs || {};
      setVideoImageUrl(videoData?.imageUrl || '');
      setGifImageUrl(gifData?.imageUrl || '');
      setRawVideos(renderVideos(videoData));
      setRawGifs(renderGifs(gifData));
      setFormData(currFields);
    }
  }, [category]);

  const validate = () => {
    const updatedFields = {};
    const isValid = formDataValidate(formData, updatedFields);
    setFormData(updatedFields);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      dispatch(stepUpLoaderCount());
      toggleDrawer('', false, '');
      const selectedLanguage = decryptLocalStorage('lang');
      const language = selectedLanguage !== 'en' ? selectedLanguage : '';
      try {
        const images = await getProcessedImages(
          rawImages,
          originalHotels.find((hotel) => hotel?.id === hotelId)?.groupId,
          SPA_TYPE,
          hotelId,
        );
        const gifDataArr = await processGifsForUpload({
          rawGifs,
          groupId,
          propertyType: SPA_TYPE,
          hotelId,
        });
        const mediaContent = toMediaContent({
          videoList: rawVideos,
          videoImageUrl,
          gifList: gifDataArr,
          gifImageUrl,
        });
        const payload = {
          name: formData?.name?.value,
          code: formData?.code?.value,
          spaId: formData?.spaId?.value,
          customAttributes: formData?.customAttributes?.value,
          images,
          mediaContent,
          videos: { imageUrl: videoImageUrl || '' },
          gifs: { imageUrl: gifImageUrl || '' },
        };
        if (category) {
          payload.id = category?.id;
          await updateSpaCategoryAPI(payload, hotelId, language);
          notificationManager('success', 'Category updated successfully');
        } else {
          await createSpaCategoryAPI(payload, hotelId, language);
          notificationManager('success', 'Category created successfully');
        }
        dispatch(fetchAllSpa(hotelId));
      } catch (error) {
        if (error?.graphQLErrors) {
          notificationManager('danger', error?.graphQLErrors?.[0]?.message);
        } else {
          category
            ? notificationManager('danger', 'Failed to update Category')
            : notificationManager('danger', 'Failed to create Category');
        }
      } finally {
        dispatch(stepDownLoaderCount());
      }
    } else {
      notificationManager('danger', 'Enter all required fields');
      if (!validate(1)) {
        setTabValue(1);
      }
      if (!validate(0)) {
        setTabValue(0);
      }
    }
  };

  const onCancel = (e) => {
    toggleDrawer('', false, '');
  };

  const handleTabChange = (event, newValue) => {
    if (newValue < tabValue) {
      setTabValue(newValue);
    } else {
      if (validate(tabValue)) {
        setTabValue(newValue);
      }
    }
  };

  const onNextClick = () => {
    if (validate(tabValue)) {
      setTabValue(tabValue + 1);
    }
  };

  const onImageListUpdated = (imgObjArr) => {
    setRawImages(imgObjArr);
  };

  const onUploadedImageRemoval = (index) => {
    const next = [...formData.images.value];
    next.splice(index, 1);
    setFormData({ images: { ...formData.images, value: next } });
  };

  return (
    <form className='category-create' noValidate>
      <div className='container pt-4 d-flex align-items-end flex-column '>
        <div className='row px-3 w-100 mt-4'>
          <div className='col-md-6'>
            <span className='page-title'>{category ? 'Edit Category' : 'Add Category'}</span>
          </div>
          <div className='col-md-5'>
            <Tabs
              value={tabValue}
              variant='fullWidth'
              indicatorColor='primary'
              onChange={handleTabChange}
              aria-label='roomTypes-add-tabs'
              textColor='primary'
              className='property-tabs'
            >
              <Tab label='Details' />
              <Tab label='Media' />
            </Tabs>
          </div>
          <div className='col-md-1 d-flex justify-content-end'>
            <IconButton aria-label='delete' onClick={(e) => onCancel(e)}>
              <CancelIcon />
            </IconButton>
          </div>
        </div>
        {tabValue === 0 && (
          <div className='row px-3 w-100 d-flex mt-4 form-scroll'>
            <div className='row px-3 w-100 mt-4 '>
              <div className='col-md-6'>
                <span className='page-title-secondary'>Details</span>
              </div>
            </div>
            <div className='row px-3 w-100 d-flex mt-3'>
              <div className='row'>
                <div className='col-md-6 mb-3'>
                  <CustomSelect
                    item={formData.spaId}
                    handleInputChange={(e) => {
                      setFormData({ spaId: e });
                    }}
                    labelField='name'
                    valueField='id'
                    options={originalSpaInfo}
                    hideNullOption={originalSpaInfo.length}
                  />
                </div>
              </div>
              <div className='col-md-6 mb-3'>
                <CustomTextField
                  item={formData.name}
                  handleInputChange={(e) => {
                    setFormData({ name: e });
                  }}
                  maxLength={100}
                  endAdornment={true}
                  characterCount={true}
                  multiline={true}
                />
              </div>
              <div className='col-md-6 mb-3'>
                <CustomTextField
                  item={formData?.code}
                  handleInputChange={(e) => {
                    setFormData({ code: e });
                  }}
                  multiline={true}
                  maxLength={8}
                  endAdornment={true}
                  characterCount={true}
                />
              </div>
            </div>
            <KeyValuePairList
              item={formData?.customAttributes}
              onListChange={(e) => {
                setFormData({ customAttributes: e });
              }}
            />
          </div>
        )}
        {tabValue === 1 && (
          <div className='row px-3 w-100 d-flex mt-4 form-scroll'>
            <div className='col-md-12 mb-3'>
              <h4 className='tab-title '>Media</h4>
              <ImageUpload
                uploadedImages={formData?.images?.value}
                imgList={rawImages}
                onImageListUpdated={onImageListUpdated}
                onUploadedImageRemoval={onUploadedImageRemoval}
                onImagesLoading={(status) => {
                  setLoading(status);
                }}
                videoImageUrl={videoImageUrl}
                onVideoImageUrlChange={setVideoImageUrl}
                gifImageUrl={gifImageUrl}
                onGifImageUrlChange={setGifImageUrl}
                videoList={rawVideos}
                gifList={rawGifs}
                onVideoListUpdated={setRawVideos}
                onGifListUpdated={setRawGifs}
                hotelId={hotelId}
                groupId={groupId}
                propertyType={SPA_TYPE}
                onVideoError={setHasVideoError}
                onThumbnailUploading={setIsThumbnailUploading}
                onVideoLoading={setIsVideoLoading}
                onGifLoading={setIsGifLoading}
              />
            </div>
          </div>
        )}
        <div className='row mx-2 mt-auto px-4 w-100 pb-5'>
          <div className='col-md-12 d-flex justify-content-start'>
            {tabValue < 1 ? (
              <Button
                variant='contained'
                color='primary'
                onClick={onNextClick}
                size='large'
                className='me-3'
                disabled={isTabInValid || hasVideoError || isThumbnailUploading || isVideoLoading || isGifLoading}
              >
                Next
              </Button>
            ) : (
              <Button
                disabled={loading || hasVideoError || isThumbnailUploading || isVideoLoading || isGifLoading}
                onClick={handleSubmit}
                variant='contained'
                color='primary'
                className='me-3'
                size='large'
              >
                {category ? 'Update' : 'Add'}
              </Button>
            )}
            <Button
              disabled={loading}
              variant='outlined'
              type='button'
              color='secondary'
              size='large'
              onClick={(e) => onCancel(e)}
            >
              Cancel
            </Button>
          </div>
          {loading && <div className='pt-4 col-md-12'>{<LinearProgressBar />}</div>}
        </div>
      </div>
    </form>
  );
};

export default CategoryCreate;
